"use client";

import { useState } from "react";
import { purchase, type LinkBoxPlanKey } from "@/lib/revenuecat/client";
import { createClient } from "@/lib/supabase/client";

interface Props {
  plan: LinkBoxPlanKey;
  cycle?: "monthly" | "annual";
  /** Tailwind classes for the rendered button. */
  className?: string;
  children: React.ReactNode;
}

/**
 * Smart CTA. If the visitor is signed in, fires the RevenueCat Web
 * Billing purchase flow directly using their Supabase user id. If not,
 * redirects to /signup?plan=…&cycle=… so the signup flow can complete
 * the purchase post-auth. (The hardened get-service-config rejects
 * anon-only callers, so an unauthenticated direct-purchase path isn't
 * possible without loosening security.)
 */
export function SubscribeButton({
  plan,
  cycle = "annual",
  className,
  children,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setError(null);
    setBusy(true);
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        const params = new URLSearchParams({ plan, cycle });
        window.location.href = `/signup?${params.toString()}`;
        return;
      }
      await purchase(plan, cycle, user.id);
      // RC redirects on success — no further action needed here.
    } catch (e) {
      setError(e instanceof Error ? e.message : "Purchase failed");
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className={className}
      >
        {busy ? "Opening checkout…" : children}
      </button>
      {error && (
        <p className="text-xs text-red-400 mt-2" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
